import React from 'react';
import { motion, Variants } from 'framer-motion';
import './ChartCard.css';
import SkeletonLoader from './SkeletonLoader';

interface ChartCardProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  loading?: boolean;
  onRefresh?: () => void;
  className?: string;
  gridColumn?: string;
  index?: number;
}

/* =========================
   ANIMATION VARIANTS
========================= */

const containerVariants: Variants = {
  hidden: { opacity: 0, y: 20 },

  visible: (index = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      delay: index * 0.1,
      ease: [0.16, 1, 0.3, 1],
    },
  }),
};

const ChartCard: React.FC<ChartCardProps> = ({
  title,
  subtitle,
  children,
  loading = false,
  onRefresh,
  className = '',
  gridColumn,
  index = 0,
}) => {
  if (loading) {
    return (
      <div className={`chart-card chart-card-loading ${className}`} style={{ gridColumn }}>
        <div className="chart-card-header">
          <SkeletonLoader type="text" width="150px" height="18px" />
        </div>

        <SkeletonLoader type="chart" height="300px" />
      </div>
    );
  }

  return (
    <motion.div
      className={`chart-card ${className}`}
      style={{ gridColumn }}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      custom={index}
      whileHover={{
        y: -4,
        transition: { duration: 0.2, ease: [0.16, 1, 0.3, 1] },
      }}
    >
      {/* HEADER */}
      <div className="chart-card-header">
        <div className="chart-card-title-section">
          <h3 className="chart-card-title">{title}</h3>
          {subtitle && <p className="chart-card-subtitle">{subtitle}</p>}
        </div>

        {onRefresh && (
          <button className="btn-chart-refresh" onClick={onRefresh} title="Refresh Chart">
            <i className="bx bx-revision"></i>
          </button>
        )}
      </div>

      {/* CONTENT */}
      <div className="chart-card-content">{children}</div>
    </motion.div>
  );
};

export default ChartCard;
